import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { resetApiState } from "../../redux/api/apiActions";
import { arClass } from "../../utils/useArClass";


export default function SubmitLoading({
  isOpen = false,
  message = "Processing your request...",
  subMessage = "Please don't close this window",
}) {
  const dispatch = useDispatch();
  const { requests } = useSelector((s) => s.api);
  const darkMode = useSelector((s) => s.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const error = requests['default']?.error;
 


  // Handle showing/hiding loading state only when open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;

    if (error) {
      dispatch(resetApiState());
    }

    if (isOpen && !error) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = prevOverflow;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, error]);

 

  return (

      <AnimatePresence>
  
          <motion.div
            key="submit-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ 
              opacity: 0,
              scale: 0.9,
              transition: { duration: 0.3, ease: "easeInOut" }
            }}
            className={arClass("fixed inset-0 z-[1000] flex items-center justify-center")}
          >
            {/* Dimmed background with exit animation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.3 } }}
              className={arClass("absolute inset-0 bg-black/40 backdrop-blur-sm")}
            />

            {/* Dialog with exit animation */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ 
                scale: 0.95, 
                opacity: 0,
                transition: { duration: 0.3, ease: "easeInOut" }
              }}
              className={arClass(`relative z-10 w-full max-w-md p-6 rounded-2xl border shadow-xl flex flex-col items-center max-sm:mx-2
                ${darkMode ? "bg-gray-800/95 border-gray-700" : "bg-white/95 border-gray-200"}`)}
            >
              {/* 🕒 Clock loading animation */}
              <div className="relative w-24 h-24 flex items-center justify-center mb-4 max-sm:text-xs">
                {/* Glow behind the clock */}
                <motion.div
                  className={arClass("absolute inset-0 rounded-full")}
                  style={{
                    background: darkMode
                      ? "radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%)"
                      : "radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)",
                  }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Clock face */}
                <div
                  className={arClass(`absolute inset-0 rounded-full border-[3px] shadow-inner ${
                    darkMode
                      ? "border-gray-600 bg-gray-900 shadow-gray-800"
                      : "border-gray-300 bg-white shadow-gray-200"
                  }`)}
                />

                {/* Ticking center dot */}
                <motion.div
                  className={arClass(`absolute w-3 h-3 rounded-full ${
                    darkMode ? "bg-blue-400" : "bg-blue-500"
                  }`)}
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Hour hand */}
                <motion.div
                  className={arClass("absolute top-1/2 left-1/2 w-1 h-8 rounded-full origin-top")}
                  style={{
                    backgroundColor: darkMode ? "#9CA3AF" : "#374151",
                    boxShadow: darkMode
                      ? "0 0 6px rgba(255,255,255,0.3)"
                      : "0 0 4px rgba(0,0,0,0.3)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 12, ease: "linear" }}
                />

                {/* Minute hand */}
                <motion.div
                  className={arClass("absolute top-1/2 left-1/2 w-1 h-10 rounded-full origin-top")}
                  style={{
                    backgroundColor: darkMode ? "#3B82F6" : "#2563EB",
                    boxShadow: "0 0 8px rgba(59,130,246,0.5)",
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 3, ease: "linear" }}
                />

                {/* Dotted second track */}
                <motion.div
                  className={arClass("absolute inset-0 rounded-full border-2 border-dotted")}
                  style={{
                    borderColor: darkMode
                      ? "rgba(59,130,246,0.3)"
                      : "rgba(59,130,246,0.4)",
                  }}
                  animate={{ rotate: -360 }}
                  transition={{ repeat: Infinity, duration: 6, ease: "linear" }}
                />

                {/* Numbers */}
                <div
                  className={arClass(`absolute inset-0 flex items-center justify-center text-[10px] font-semibold ${
                    darkMode ? "text-gray-400" : "text-gray-500"
                  }`)}
                >
                  <span className="absolute top-1">12</span>
                  <span className="absolute right-1 top-1/2 -translate-y-1/2">3</span>
                  <span className="absolute bottom-1">6</span>
                  <span className="absolute left-1 top-1/2 -translate-y-1/2">9</span>
                </div>
              </div>

              {/* ✨ Message Section */}
              <div className={arClass("text-center mb-3")}>
                <div
                  className={arClass(`text-lg font-semibold max-sm:text-sm ${
                    darkMode ? "text-gray-100" : "text-gray-900"
                  }`)}
                >
                  {message}
                </div>
                {subMessage && (
                  <div
                    className={arClass(`text-sm mt-1 max-sm:text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`)}
                  >
                    {
                      defaultLanguage === 'ar' ? 'يرجى عدم إغلاق هذه النافذة' :defaultLanguage ==='fr' ? "Veuillez ne pas fermer cette fenêtre" : subMessage
                    }
                  </div>
                )}
              </div>

              {/* Loading Dots */}
              <div className={arClass("flex space-x-1 mt-2 max-sm:justify-center max-sm:items-center")}>
                {[0, 0.2, 0.4].map((delay, i) => (
                  <motion.div
                    key={i}
                    className={arClass(`w-2 h-2 rounded-full ${
                      darkMode ? "bg-blue-400" : "bg-blue-500"
                    }`)}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1.5,
                      delay,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        
      </AnimatePresence>
  );
}